/**
 * Ausgelagerte KMVS-Fenster: echte Browserfenster (`window.open`), deren
 * Inhalt im React-Baum des Hauptfensters weiterläuft (User 26.10.2026: „aus
 * einem Fenster ein Browser-Window, um es einem anderen Bildschirm zuzuordnen").
 *
 * Der Inhalt bleibt gemountet und zieht nur als DOM um. Dabei verlässt er das
 * Dokument, von dem der Code ausgeht: `document`, `window` und
 * `window.getSelection()` meinen weiterhin das Hauptfenster. Dieses Modul ist
 * die eine Stelle, die von den offenen Browserfenstern weiß:
 *
 * - `alleDokumente` / `alleFenster` statt `document.addEventListener` bzw.
 *   `window.addEventListener`: Bedien-Ereignisse (Klicks, Tasten, Fokus …)
 *   kommen dann auch aus den Browserfenstern an. Sonst schlösse z. B. ein Menü
 *   im Browserfenster nie beim Klick daneben, und Esc liefe ins Leere.
 * - `aktivesDokument()` / `aktiveAuswahl()` statt `document` bzw.
 *   `window.getSelection()` für Auswahl, `execCommand` und Fokus: Der Mail-
 *   Editor arbeitet sonst auf der (leeren) Auswahl des Hauptfensters.
 */

/** Ereignisse, die aus jedem offenen Browserfenster mitgehört werden. */
const SPIEGEL_ARTEN = new Set([
  "mousedown", "mouseup", "mousemove", "click", "dblclick", "contextmenu", "auxclick",
  "pointerdown", "pointerup", "pointermove", "pointercancel",
  "touchstart", "touchmove", "touchend", "touchcancel",
  "keydown", "keyup", "keypress",
  "focusin", "focusout",
  "wheel", "scroll", "resize",
  "selectionchange", "input", "beforeinput",
  "copy", "cut", "paste",
  "dragstart", "dragenter", "dragover", "dragleave", "drop", "dragend",
]);

type Ziel = "dokument" | "fenster";
type Eintrag = {
  ziel: Ziel;
  art: string;
  listener: EventListenerOrEventListenerObject;
  optionen?: boolean | AddEventListenerOptions;
};

const offene = new Set<Window>();
const eintraege: Eintrag[] = [];
/** Dokument, in dem zuletzt geklickt, getippt oder fokussiert wurde; `null` = Hauptfenster. */
let aktiv: Document | null = null;

const capture = (o?: boolean | EventListenerOptions) => (typeof o === "boolean" ? o : !!o?.capture);

const zielVon = (win: Window, ziel: Ziel): Window | Document => (ziel === "dokument" ? win.document : win);

function haengeAn(win: Window, e: Eintrag) {
  try {
    zielVon(win, e.ziel).addEventListener(e.art, e.listener, e.optionen);
  } catch {
    // Browserfenster schon zu — nichts zu tun.
  }
}

function haengeAb(win: Window, ziel: Ziel, art: string, listener: EventListenerOrEventListenerObject, optionen?: boolean | EventListenerOptions) {
  try {
    zielVon(win, ziel).removeEventListener(art, listener, optionen);
  } catch {
    // Browserfenster schon zu — nichts zu tun.
  }
}

function hinzufuegen(
  ziel: Ziel,
  haupt: Window | Document,
  art: string,
  listener: EventListenerOrEventListenerObject | null,
  optionen?: boolean | AddEventListenerOptions,
) {
  if (!listener) return;
  haupt.addEventListener(art, listener, optionen);
  if (!SPIEGEL_ARTEN.has(art)) return;
  const eintrag: Eintrag = { ziel, art, listener, optionen };
  for (const win of offene) haengeAn(win, eintrag);
  // `once` räumt der Browser selbst ab — ein später geöffnetes Browserfenster
  // bekäme den Listener sonst noch einmal.
  if (typeof optionen === "object" && optionen.once) return;
  eintraege.push(eintrag);
  const signal = typeof optionen === "object" ? optionen.signal : undefined;
  signal?.addEventListener("abort", () => entfernen(ziel, haupt, art, listener, optionen), { once: true });
}

function entfernen(
  ziel: Ziel,
  haupt: Window | Document,
  art: string,
  listener: EventListenerOrEventListenerObject | null,
  optionen?: boolean | EventListenerOptions,
) {
  if (!listener) return;
  haupt.removeEventListener(art, listener, optionen);
  if (!SPIEGEL_ARTEN.has(art)) return;
  const c = capture(optionen);
  for (let i = eintraege.length - 1; i >= 0; i--) {
    const e = eintraege[i];
    if (e.ziel === ziel && e.art === art && e.listener === listener && capture(e.optionen) === c) eintraege.splice(i, 1);
  }
  for (const win of offene) haengeAb(win, ziel, art, listener, optionen);
}

type ListenerApi = Pick<Document, "addEventListener" | "removeEventListener">;
type FensterListenerApi = Pick<Window, "addEventListener" | "removeEventListener">;

/** Wie `document.addEventListener`, hört aber auch in ausgelagerten Browserfenstern mit. */
export const alleDokumente = {
  addEventListener(art: string, listener: EventListenerOrEventListenerObject | null, optionen?: boolean | AddEventListenerOptions) {
    hinzufuegen("dokument", document, art, listener, optionen);
  },
  removeEventListener(art: string, listener: EventListenerOrEventListenerObject | null, optionen?: boolean | EventListenerOptions) {
    entfernen("dokument", document, art, listener, optionen);
  },
} as ListenerApi;

/** Wie `window.addEventListener`, hört aber auch in ausgelagerten Browserfenstern mit. */
export const alleFenster = {
  addEventListener(art: string, listener: EventListenerOrEventListenerObject | null, optionen?: boolean | AddEventListenerOptions) {
    hinzufuegen("fenster", window, art, listener, optionen);
  },
  removeEventListener(art: string, listener: EventListenerOrEventListenerObject | null, optionen?: boolean | EventListenerOptions) {
    entfernen("fenster", window, art, listener, optionen);
  },
} as FensterListenerApi;

/** Alle offenen, ausgelagerten Browserfenster. */
export function browserFenster(): Window[] {
  return [...offene].filter((w) => !w.closed);
}

/**
 * Dokument, in dem der Nutzer gerade arbeitet: das Browserfenster, in dem
 * zuletzt geklickt, getippt oder fokussiert wurde — sonst das Hauptfenster.
 */
export function aktivesDokument(): Document {
  if (aktiv && aktiv.defaultView && offene.has(aktiv.defaultView) && !aktiv.defaultView.closed) return aktiv;
  return document;
}

/**
 * Fenster, in dem gerade gearbeitet wird — für Menüs und Popups, die sich an
 * `innerWidth`/`innerHeight` ausrichten (sonst ragten sie in einem kleineren
 * Browserfenster über den Rand).
 */
export function aktivesFenster(): Window {
  return aktivesDokument().defaultView ?? window;
}

/** Statt `window.getSelection()`: die Auswahl des Dokuments, in dem gerade gearbeitet wird. */
export function aktiveAuswahl(): Selection | null {
  return aktivesDokument().getSelection();
}

/**
 * Das ausgelagerte Browserfenster, in dem gerade gearbeitet wird — `undefined`
 * im Hauptfenster. Für Fenster, die aus einem Fensterinhalt heraus aufgehen
 * und dann neben ihm erscheinen sollen (`OpenWindowConfig.browserFensterNeben`).
 */
export function ausgelagertesFenster(): Window | undefined {
  const win = aktivesFenster();
  return win === window ? undefined : win;
}

/**
 * Statt `document.getElementById`: sucht zuerst im Dokument, in dem gerade
 * gearbeitet wird, dann im Hauptfenster und in den übrigen Browserfenstern.
 */
export function elementMitId(id: string): HTMLElement | null {
  const aktivDok = aktivesDokument();
  const treffer = aktivDok.getElementById(id) ?? (aktivDok === document ? null : document.getElementById(id));
  if (treffer) return treffer;
  for (const w of browserFenster()) {
    const el = w.document.getElementById(id);
    if (el) return el;
  }
  return null;
}

/** Je Browserfenster der Listener, der es als aktiv vormerkt. */
const aktivMerker = new WeakMap<Window, () => void>();

const merkeHaupt = () => {
  aktiv = null;
};

/**
 * Meldet ein frisch geöffnetes Browserfenster an: Ab jetzt hören alle über
 * `alleDokumente`/`alleFenster` angemeldeten Listener dort mit. Liefert die
 * Abmeldung zurück (beim Schließen bzw. Zurückholen aufrufen).
 */
export function meldeBrowserFensterAn(win: Window): () => void {
  if (offene.has(win)) return () => meldeBrowserFensterAb(win);
  offene.add(win);
  for (const e of eintraege) haengeAn(win, e);
  const merke = () => {
    aktiv = win.document;
  };
  const doc = win.document;
  for (const art of ["pointerdown", "keydown", "focusin"]) doc.addEventListener(art, merke, true);
  win.addEventListener("focus", merke);
  aktivMerker.set(win, merke);
  return () => meldeBrowserFensterAb(win);
}

export function meldeBrowserFensterAb(win: Window) {
  if (!offene.delete(win)) return;
  for (const e of eintraege) haengeAb(win, e.ziel, e.art, e.listener, e.optionen);
  const merke = aktivMerker.get(win);
  if (merke) {
    try {
      for (const art of ["pointerdown", "keydown", "focusin"]) win.document.removeEventListener(art, merke, true);
      win.removeEventListener("focus", merke);
    } catch {
      // schon zu
    }
  }
  if (aktiv === win.document) aktiv = null;
}

if (typeof document !== "undefined") {
  for (const art of ["pointerdown", "keydown", "focusin"]) document.addEventListener(art, merkeHaupt, true);
  window.addEventListener("focus", merkeHaupt);
}

/** Nur für Tests: Zustand zurücksetzen. */
export function __setzeRegisterZurueck() {
  for (const w of [...offene]) meldeBrowserFensterAb(w);
  eintraege.length = 0;
  aktiv = null;
}
